/**
 * Hook for dynamically positioning campaign zone labels
 * 
 * Labels are centered on the zone border (straddling the line).
 * They stick to the closest visible border section, avoiding HUD elements.
 * 
 * UX PATTERN:
 * - Labels live in world space, so they travel with the canvas during a gesture
 * - After the camera settles they re-pin to the visible zone edge
 * - When changing edges, labels fade out/in instead of flying across
 * 
 * IMPORTANT: During fast panning, the DOM transform is updated directly (bypassing React state).
 * We track the "live" pan values from panupdate events separately from React state.
 */

import { useState, useEffect, useCallback, useRef } from 'react';

import {
  DEBUG_LABEL_POSITIONING,
} from './utils/constants';

import {
  getViewportBounds,
  getExclusionZonesInCanvas,
} from './utils/geometry';

import {
  calculateLabelPosition,
} from './utils/labelCalculation';

// Debounce time for HUD element changes (ms)
const HUD_CHANGE_DEBOUNCE = 150;
// Re-pin labels after the camera stops — setState every pan frame stutters a large flow
const PAN_LABEL_IDLE_MS = 100;

/**
 * Hook to manage label positions for all campaign zones
 */
export function useLabelPositioning({
  campaignZones,
  containerRef,
  pan,
  zoom,
  isAnimating = false, // Skip calculations during animation to prevent stutter
}) {
  const [labelPositions, setLabelPositions] = useState({});
  const mutationObserverRef = useRef(null);
  const hudChangeTimerRef = useRef(null);
  const panLabelIdleRef = useRef(null);
  
  // Store campaign zones in ref for callbacks
  const zonesRef = useRef(campaignZones);
  
  // Track animation state in ref for callbacks
  const isAnimatingRef = useRef(isAnimating);
  isAnimatingRef.current = isAnimating;
  
  // CRITICAL: Track the "live" pan/zoom values from panupdate events
  // These may differ from React state during fast panning (DOM is updated directly)
  const livePanRef = useRef(pan);
  const liveZoomRef = useRef(zoom);
  
  // Update zones ref
  useEffect(() => {
    zonesRef.current = campaignZones;
  }, [campaignZones]);
  
  // Update live refs when React state changes (for when not actively panning)
  useEffect(() => {
    livePanRef.current = pan;
    liveZoomRef.current = zoom;
  }, [pan, zoom]);
  
  /**
   * Calculate and set all label positions
   */
  const updatePositions = useCallback((currentPan, currentZoom) => {
    // Skip calculations during animation to prevent stutter
    if (isAnimatingRef.current) {
      return;
    }
    
    if (!containerRef?.current || zonesRef.current.length === 0) {
      return;
    }
    
    const containerRect = containerRef.current.getBoundingClientRect();
    const viewport = getViewportBounds(containerRect, currentPan, currentZoom);
    if (!viewport) return;
    
    const exclusionZones = getExclusionZonesInCanvas(containerRect, currentPan, currentZoom);
    
    if (DEBUG_LABEL_POSITIONING) {
      console.log('[LabelPositioning] Calculating positions', { pan: currentPan, zoom: currentZoom });
    }
    
    const newPositions = {};
    
    for (const zone of zonesRef.current) {
      const campaignName = zone.campaign?.name || zone.id;
      
      const pos = calculateLabelPosition(
        zone.bounds, 
        viewport, 
        exclusionZones,
        currentZoom,
        campaignName
      );
      
      newPositions[zone.id] = pos;
    }
    
    setLabelPositions(newPositions);
  }, [containerRef]);
  
  /**
   * Keep live pan/zoom refs current during a gesture; re-pin labels after it idles.
   */
  useEffect(() => {
    const canvas = containerRef?.current?.querySelector('.flowchart__canvas');
    if (!canvas) return;

    const flushLabels = () => {
      if (panLabelIdleRef.current) {
        clearTimeout(panLabelIdleRef.current);
        panLabelIdleRef.current = null;
      }
      updatePositions(livePanRef.current, liveZoomRef.current);
    };
    
    const handlePanUpdate = (e) => {
      const { x, y, zoom: currentZoom } = e.detail;
      livePanRef.current = { x, y };
      liveZoomRef.current = currentZoom;

      if (panLabelIdleRef.current) clearTimeout(panLabelIdleRef.current);
      panLabelIdleRef.current = setTimeout(flushLabels, PAN_LABEL_IDLE_MS);
    };
    
    canvas.addEventListener('panupdate', handlePanUpdate);
    window.addEventListener('flowchartpanend', flushLabels);
    
    return () => {
      canvas.removeEventListener('panupdate', handlePanUpdate);
      window.removeEventListener('flowchartpanend', flushLabels);
      if (panLabelIdleRef.current) {
        clearTimeout(panLabelIdleRef.current);
        panLabelIdleRef.current = null;
      }
    };
  }, [containerRef, updatePositions]);
  
  /**
   * Observe HUD element changes (show/hide/resize)
   * Uses livePanRef/liveZoomRef to get correct values even during fast panning
   */
  useEffect(() => {
    const container = containerRef?.current;
    if (!container) return;
    
    const handleHudChange = () => {
      if (hudChangeTimerRef.current) {
        clearTimeout(hudChangeTimerRef.current);
      }
      
      hudChangeTimerRef.current = setTimeout(() => {
        if (DEBUG_LABEL_POSITIONING) {
          console.log('[LabelPositioning] HUD changed');
        }
        // Use live refs - these have the actual current values
        updatePositions(livePanRef.current, liveZoomRef.current);
      }, HUD_CHANGE_DEBOUNCE);
    };
    
    mutationObserverRef.current = new MutationObserver((mutations) => {
      // Filter out mutations that are just the canvas transform changing
      // We only care about HUD element changes
      const hudChanged = mutations.some(mutation => {
        const target = mutation.target;
        
        // Skip canvas transform changes - these are handled by panupdate
        if (target.classList?.contains('flowchart__canvas')) {
          return false;
        }
        
        // Check if this is a HUD element change
        if (mutation.type === 'attributes' || mutation.type === 'childList') {
          if (target.closest?.('.flowchart-hud') || 
              target.closest?.('.flowchart-minimap') ||
              target.closest?.('.campaign-legend')) {
            return true;
          }
        }
        return false;
      });
      
      if (hudChanged) {
        handleHudChange();
      }
    });
    
    mutationObserverRef.current.observe(container, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['class', 'style'],
    });
    
    return () => {
      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect();
      }
      if (hudChangeTimerRef.current) {
        clearTimeout(hudChangeTimerRef.current);
      }
    };
  }, [containerRef, updatePositions]);
  
  // Initial update and update when zones change
  useEffect(() => {
    updatePositions(livePanRef.current, liveZoomRef.current);
  }, [campaignZones, updatePositions]);
  
  // Update when React state pan/zoom changes
  // This handles cases where pan/zoom changes without panupdate events (e.g., programmatic changes)
  useEffect(() => {
    // Update live refs
    livePanRef.current = pan;
    liveZoomRef.current = zoom;
    // Update positions
    updatePositions(pan, zoom);
  }, [pan, zoom, updatePositions]);
  
  return labelPositions;
}

export default useLabelPositioning;
